// Bridge between cartReducer and CartOperationManager
import { Dispatch } from "redux";
import CartOperationManager, {
  OperationType,
} from "../services/CartOperationManager";
import {
  setDeliveryFeesLoading,
  resetDeliveryFeesLoading,
  setGroupDeliveryFeeLoading,
  resetGroupDeliveryFeeLoading,
  incrementPendingFeeApiCalls,
  decrementPendingFeeApiCalls,
  // Add new operation-based actions
  startLocationUpdate,
  completeLocationUpdate,
  startFeeCalculation,
  completeFeeCalculation,
  startDateUpdate,
  completeDateUpdate,
  startItemUpdate,
  completeItemUpdate,
} from "./cartReducer";

/**
 * Adapter to use the new CartOperationManager with existing Redux actions
 */
export const registerLocationUpdate = (
  dispatch: Dispatch,
  destinationZoneId: string,
  groupIds: string[]
) => {
  // Register with operation manager
  const operationId = CartOperationManager.registerOperation(
    "LOCATION_UPDATE",
    { destinationZoneId },
    groupIds
  );

  // Dispatch both new and legacy actions for compatibility
  dispatch(startLocationUpdate(groupIds)); // New operation-based action

  // Legacy actions are now redundant but kept for backward compatibility until all components are updated
  dispatch(setDeliveryFeesLoading(true));
  groupIds.forEach((groupId) => {
    dispatch(setGroupDeliveryFeeLoading({ groupId, isLoading: true }));
  });
  dispatch(incrementPendingFeeApiCalls());

  return {
    operationId,
    complete: (success = true) => {
      if (success) {
        CartOperationManager.completeOperation(operationId);
      } else {
        CartOperationManager.failOperation(operationId);
      }

      // Dispatch both new and legacy actions for compatibility
      dispatch(completeLocationUpdate());

      // Legacy actions
      groupIds.forEach((groupId) => {
        dispatch(resetGroupDeliveryFeeLoading(groupId));
      });
      dispatch(decrementPendingFeeApiCalls());
      dispatch(resetDeliveryFeesLoading({ force: false }));
    },
  };
};

// Add similar adapters for other operations
export const registerFeeCalculation = (dispatch: Dispatch, groupId: string) => {
  const operationId = CartOperationManager.registerOperation(
    "FEE_CALCULATION" as OperationType,
    {},
    [groupId]
  );

  dispatch(startFeeCalculation([groupId]));

  return {
    operationId,
    complete: (success = true) => {
      if (success) {
        CartOperationManager.completeOperation(operationId);
      } else {
        CartOperationManager.failOperation(operationId);
      }

      dispatch(completeFeeCalculation(groupId));
    },
  };
};

export const registerDateUpdate = (
  dispatch: Dispatch,
  groupId: string,
  date: string
) => {
  const operationId = CartOperationManager.registerOperation(
    "DATE_UPDATE",
    { date },
    [groupId]
  );

  dispatch(startDateUpdate(groupId));

  return {
    operationId,
    complete: (success = true) => {
      if (success) {
        CartOperationManager.completeOperation(operationId);
      } else {
        CartOperationManager.failOperation(operationId);
      }

      dispatch(completeDateUpdate(groupId));
    },
  };
};

export const registerItemUpdate = (
  dispatch: Dispatch,
  groupId: string,
  itemId: string,
  quantity: number
) => {
  // Add itemId as last parameter to track specific items
  const operationId = CartOperationManager.registerOperation(
    quantity > 0 ? "QUANTITY_UPDATE" : "ITEM_REMOVAL",
    { cartCustomId: itemId, quantity },
    [groupId],
    itemId // Pass the itemId to track specific item
  );

  dispatch(startItemUpdate({ itemId, groupId }));

  return {
    operationId,
    complete: (success = true) => {
      if (success) {
        CartOperationManager.completeOperation(operationId);
      } else {
        CartOperationManager.failOperation(operationId);
      }

      dispatch(completeItemUpdate({ itemId, groupId }));
    },
  };
};
